<template>
  <div>
    <div class="d-flex align-center mb-3">
      <v-icon icon="$pushPinXs" class="mr-2" />
      <div class="subtitle1">{{ $t("ticketDetails.keyDetails.title") }}</div>
    </div>

    <section v-if="showWorkspaceBlock" class="mb-1">
      <div v-if="props.ticketDetails.workspaceName" class="mb-1">
        <span class="subtitle2">{{ $t("ticketDetails.customer") }}: </span>
        <span class="body2 coro-link" @click="handleWorkspaceClick">
          {{ props.ticketDetails.workspaceName }}
        </span>
      </div>
      <div v-if="props.ticketDetails.workspaceId">
        <span class="subtitle2">{{ $t("ticketDetails.customerId") }}: </span>
        <span class="body2" data-testid="ticket-preview-last-seen">
          {{ props.ticketDetails.workspaceId }}
        </span>
      </div>
    </section>

    <component
      v-if="resolvedComponent"
      :is="resolvedComponent"
      :ticket-details="props.ticketDetails"
      :is-soc="props.isSoc"
      :context="props.context"
    />
  </div>
</template>

<script setup lang="ts" generic="T extends TicketDetails = TicketDetails">
import { type Component, computed } from "vue";
import { useRouter } from "vue-router";
import { RouteName } from "@/constants/routes";
import { TicketType } from "@/constants/tickets";
import { AppContext } from "@/types";
import type { TicketDetails } from "@/_store/tickets/tickets.module";
import type { MspTicketDetails } from "@/_store/msp/tickets/msp-tickets.module";
import { useWorkspaceStore } from "@/_store/workspace.module";

import CloudAppTicketInfo from "./CloudAppTicketInfo.vue";
import DeviceTicketInfo from "./DeviceTicketInfo.vue";
import EmailTicketInfo from "./EmailTicketInfo.vue";
import UserTicketInfo from "./UserTicketInfo.vue";
import DataTicketInfo from "./DataTicketInfo.vue";
import EdrTicketInfo from "@/components/tickets/ticket-preview-details/EdrTicketInfo.vue";
import SecurityAwarenessInfo from "./SecurityAwarenessInfo.vue";
import ProxyMisconfigurationInfo from "./ProxyMisconfigurationInfo.vue";

/**
 * Configuration structure for context-specific behavior.
 * @template T - The specific type of ticket details.
 */
interface ContextConfig<T> {
  /**
   * A map of TicketTypes to their corresponding display components.
   */
  componentByType: Partial<Record<TicketType, Component>>;
  /**
   * Logic to determine if the workspace info block should be shown.
   */
  shouldShowWorkspaceBlock: (ticketDetails: T, isSoc: boolean) => boolean;
}

const props = withDefaults(
  defineProps<{
    /**
     * The main ticket details object.
     */
    ticketDetails: T;
    /**
     * The application context (e.g., 'single-workspace' or 'msp').
     */
    context: AppContext;
    /**
     * Flag indicating if the component is being viewed in a SOC context.
     */
    isSoc?: boolean;
  }>(),
  {
    isSoc: false,
  }
);

const router = useRouter();
const workspaceStore = useWorkspaceStore();

/**
 * A map of shared ticket types to their corresponding info components.
 * This is used as a base for both single-workspace and MSP contexts.
 */
const sharedComponentMap: Partial<Record<TicketType, Component>> = {
  [TicketType.MALWARE_IN_DEVICE]: DeviceTicketInfo,
  [TicketType.ENDPOINT_VULNERABILITY]: DeviceTicketInfo,
  [TicketType.WIFI_PHISHING]: DeviceTicketInfo,
  [TicketType.SUSPECTED_COMPLIANCE_VIOLATION_BY_DEVICE]: DeviceTicketInfo,
  [TicketType.SUSPECTED_UNIFIED_SENSITIVE_DATA_VIOLATION_BY_DEVICE]: DeviceTicketInfo,
  [TicketType.WIFI_FORBIDDEN_NETWORK]: DeviceTicketInfo,
  [TicketType.MALWARE_IN_EMAIL_ATTACHMENTS]: EmailTicketInfo,
  [TicketType.EMAIL_PHISHING]: EmailTicketInfo,
  [TicketType.MALICIOUS_URL_CLICK]: EmailTicketInfo,
  [TicketType.MALWARE_IN_CLOUD_DRIVE]: CloudAppTicketInfo,
  [TicketType.ACCESS_PERMISSIONS_VIOLATION]: UserTicketInfo,
  [TicketType.BLOCKED_IP_LOGIN]: UserTicketInfo,
  [TicketType.ABNORMAL_DATA_MANIPULATION]: UserTicketInfo,
  [TicketType.SUSPECTED_BOT_ATTACKS]: UserTicketInfo,
  [TicketType.ABNORMAL_ADMIN_ACTIVITY]: UserTicketInfo,
  [TicketType.SUSPECTED_IDENTITY_COMPROMISE]: UserTicketInfo,
  [TicketType.USER_INACTIVITY]: UserTicketInfo,
  [TicketType.IMPOSSIBLE_TRAVELER]: UserTicketInfo,
  [TicketType.SUSPECTED_UNIFIED_SENSITIVE_DATA_VIOLATION_BY_EMAIL]: DataTicketInfo,
  [TicketType.SUSPECTED_UNIFIED_SENSITIVE_DATA_VIOLATION_BY_SHARING]: DataTicketInfo,
  [TicketType.SUSPECTED_COMPLIANCE_VIOLATION_BY_EMAIL]: DataTicketInfo,
  [TicketType.SUSPECTED_COMPLIANCE_VIOLATION_BY_SHARING]: DataTicketInfo,
  [TicketType.SUSPECTED_DATA_EXPOSURE_BY_EMAIL]: DataTicketInfo,
  [TicketType.SUSPECTED_DATA_EXPOSURE_BY_SHARING]: DataTicketInfo,
  [TicketType.PROMPT_INJECTION]: DataTicketInfo,
  [TicketType.EDR_DETECTION]: EdrTicketInfo,
  [TicketType.BLOCKED_CONNECTION]: EdrTicketInfo,
  [TicketType.FILE_INTEGRITY_VIOLATION]: EdrTicketInfo,
  [TicketType.SIMULATED_EMAIL_PHISHING]: SecurityAwarenessInfo,
  [TicketType.SECURITY_TRAINING_FAILURE]: SecurityAwarenessInfo,
  [TicketType.PROXY_MISCONFIGURATION]: ProxyMisconfigurationInfo,
};

/**
 * Configuration specific to the single-workspace context.
 */
const singleWorkspaceConfig: ContextConfig<TicketDetails> = {
  componentByType: sharedComponentMap,
  shouldShowWorkspaceBlock: (details, isSoc) => Boolean(isSoc && details.workspaceId),
};

/**
 * Configuration specific to the MSP context.
 * It overrides some components from the shared map for MSP-specific views.
 */
const mspContextConfig: ContextConfig<MspTicketDetails> = {
  componentByType: sharedComponentMap,
  shouldShowWorkspaceBlock: (details) => Boolean(details.workspaceName || details.workspaceId),
};

/**
 * Dynamically selects the correct configuration object based on the current context.
 */
const contextConfig = computed<ContextConfig<T>>(() =>
  props.context === AppContext.MSP
    ? (mspContextConfig as ContextConfig<T>)
    : (singleWorkspaceConfig as ContextConfig<T>)
);

/**
 * Resolves the appropriate component to render based on the ticket's event type and context.
 */
const resolvedComponent = computed<Component | null>(() => {
  const component = contextConfig.value.componentByType[props.ticketDetails.eventType] ?? null;
  if (!component) {
    console.warn(`No component mapping found for ticket type: ${props.ticketDetails.eventType}`);
    return null;
  }
  return component;
});

/**
 * Determines whether to display the workspace information block.
 */
const showWorkspaceBlock = computed(() =>
  contextConfig.value.shouldShowWorkspaceBlock(props.ticketDetails, props.isSoc)
);

/**
 * Handles the click event on the workspace name.
 * It switches the active workspace and navigates to the dashboard.
 */
const handleWorkspaceClick = async () => {
  if (!props.ticketDetails.workspaceId) return;

  try {
    await workspaceStore.updateWorkspaceAndPermissions(props.ticketDetails.workspaceId, false);
    await router.push({ name: RouteName.DASHBOARD_PAGE });
  } catch (error) {
    console.error("Failed to switch workspace:", error);
  }
};
</script>
